import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import isFunction from 'lodash/isFunction';
import { useProperty } from '@eti/providers';
import { getTravelerPricesTotal } from '../../../utils/travelers';
import { addLegendNumberToSegmentEvents } from '../../../widgets/trip-details/utils/format';
import { getCartTravelerPrices } from '../../../widgets/cart/utils/travelers';
import { PAYMENT_CART_QUERY } from '../graphql/queries';
import useRefetchOnVisibilityChange from './useRefetchOnVisibilityChange';

const getTripData = ({ trip, travelers, ...rest }) => {
  const { bounds, travelerPrices, voucherAmount, type } = trip || rest;
  const travelerPrice = getCartTravelerPrices(travelers, travelerPrices);
  const travelerPriceTotal = getTravelerPricesTotal(travelerPrice);
  const boundsNew = addLegendNumberToSegmentEvents(bounds);

  return {
    bounds: boundsNew,
    voucherAmount,
    travelerPrices: travelerPrice,
    travelerPriceTotal,
    tripType: type,
  };
};

const usePaymentCart = ({
  binNumber = null,
  discountAmount,
  selectedCommercialCardId,
  selectedPaymentMethod,
  selectedPaymentMethodId,
  setRefetch,
  travelers,
  installmentOptionId,
}) => {
  const { type } = selectedPaymentMethod;
  const { p } = useProperty();

  const shouldSendInstalmentOptionId = p('Payment.CardInstallments.UI.NewFlow.Enabled');

  const { loading, error, data, refetch } = useQuery(PAYMENT_CART_QUERY, {
    fetchPolicy: 'no-cache',
    variables: {
      selectedPaymentMethod: selectedPaymentMethodId &&
        type && {
          paymentObjectId:
            selectedCommercialCardId !== null ? selectedCommercialCardId : selectedPaymentMethodId,
          paymentType: type,
          binNumber,
          ...(shouldSendInstalmentOptionId && { installmentOptionId }),
        },
    },
  });

  useRefetchOnVisibilityChange(refetch);

  useEffect(() => {
    if (!loading && data && refetch && isFunction(setRefetch)) {
      setRefetch({
        current: refetch,
      });
    }
  }, [loading, data, setRefetch, refetch, installmentOptionId]);

  if (loading) {
    return { loading };
  }

  if (error) {
    return { error };
  }

  const { cart } = data;

  if (!cart) {
    return { missingCart: true };
  }

  const { price, priceInChargeCurrency } = cart;
  const tripData = getTripData({ trip: cart.trips[0], travelers });

  return {
    ...tripData,
    loading,
    totalPrice: price.price.value,
    priceInChargeCurrency: priceInChargeCurrency?.price?.value,
    discountAmount,
    installmentOptionId,
  };
};

export default usePaymentCart;
